import { PortalRequestsPermission } from '@idc-forms/permissions'
import { CalendarClock, ClipboardCheck, Link2, RefreshCw, Send } from 'lucide-react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { useClientPortalFlows } from '@/features/client-portal/hooks/use-portal-admin'
import { FlowAuditTimeline } from '@/features/clients/components/flow-audit-timeline'
import { PortalStatusBadge } from '@/features/clients/components/portal-status-badge'
import { validationTypeLabel } from '@/features/clients/consts/portal-flow-labels'
import {
  formatFlowDate,
  SHAREABLE_FLOW_STATUSES
} from '@/features/clients/utils/portal-flow-selection'
import { Can } from '@/shared/components/can'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { Button } from '@/shared/ui/button'
import { usePortalLink } from '../hooks/use-portal-link'

// Estados desde los que el asesor puede entrar a revisar.
const REVIEWABLE = new Set(['submitted', 'resubmitted', 'under_review', 'in_progress'])

// Estados VIVOS en los que el enlace se puede reenviar tal cual ("no me llegó el correo"). El
// expirado NO va acá: para ese está "Renovar enlace" (reactiva la ventana). Espeja el backend.
const RESENDABLE_LIVE = new Set(['sent', 'opened', 'in_progress', 'changes_requested'])

type PortalFlowDetailPanelProps = {
  flowId: string
  clientId: string
  clientName: string
  clientType: 'individual' | 'juridical'
}

/**
 * Contenido del side panel de detalle de un flujo (hub del portal): estado, tipo, fechas,
 * acciones (revisar / copiar enlace / renovar) y el timeline de auditoría del flujo. El
 * flujo se toma de la lista ya cacheada por cliente (dedupe con la tabla).
 */
export function PortalFlowDetailPanel({
  flowId,
  clientId,
  clientName,
  clientType
}: PortalFlowDetailPanelProps) {
  const navigate = useNavigate()
  const { flows, extendLink, resendLink } = useClientPortalFlows(clientId)
  // ☠️ ARRIBA del early return. Lo puse abajo y `useHookAtTopLevel` lo cazó en el pre-commit: un
  // render sin flujo saldría antes de llamarlo y el siguiente lo llamaría, así que React vería dos
  // órdenes de hooks distintos en el mismo componente. No da error en la pantalla — corrompe el
  // estado, que es peor.
  const { copyLink, isCopying } = usePortalLink()
  const flow = flows?.find((f) => f.id === flowId)

  if (!flow) {
    return (
      <SidePanelSection separated={false}>
        <p className="text-sm text-muted-foreground">No se encontró el flujo.</p>
      </SidePanelSection>
    )
  }

  const isExpired = flow.status === 'expired'
  const canResend = RESENDABLE_LIVE.has(flow.status)
  const canReview = REVIEWABLE.has(flow.status)
  const reminderCount = flow.reminderCount ?? 0

  const goReview = () => {
    navigate(
      `${
        clientType === 'juridical'
          ? PATHS.admin.clients.juridical.validate(clientId)
          : PATHS.admin.clients.individual.validate(clientId)
      }/revisar`
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase text-muted-foreground">Estado</span>
            <PortalStatusBadge status={flow.status} />
          </div>
          <div className="text-xs text-muted-foreground">
            Tipo de validación: {validationTypeLabel(flow.validationType)}
          </div>

          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" />
              Creado el {formatFlowDate(flow.createdAt)}
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" />
              {isExpired ? 'Expiró el ' : 'Expira el '}
              {formatFlowDate(flow.expiresAt)}
            </div>
            <div className="mt-1">
              {reminderCount === 0
                ? 'Sin recordatorios enviados'
                : `${reminderCount} recordatorio(s) · último ${formatFlowDate(flow.lastReminderAt)}`}
            </div>
          </div>

          {canReview && (
            <Button type="button" className="w-full gap-2" onClick={goReview}>
              <ClipboardCheck className="h-4 w-4" />
              Revisar expediente
            </Button>
          )}

          {isExpired && (
            <Can permission={PortalRequestsPermission.ExtendPortalLink}>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={() => extendLink.mutate({ id: flow.id })}
                disabled={extendLink.isPending}
              >
                <RefreshCw className="h-4 w-4" />
                {extendLink.isPending ? 'Renovando…' : 'Renovar enlace'}
              </Button>
            </Can>
          )}

          {canResend && (
            <Can permission={PortalRequestsPermission.ExtendPortalLink}>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={() => resendLink.mutate(flow.id)}
                disabled={resendLink.isPending}
              >
                <Send className="h-4 w-4" />
                {resendLink.isPending ? 'Reenviando…' : 'Reenviar correo'}
              </Button>
            </Can>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={() => {
              copyLink(flow.id)
            }}
            // El estado decide, no la presencia de un token: la credencial ya no viaja en la ficha.
            // `isCopying` corta el doble click, que acá no sería inocuo — cada revelado se audita.
            disabled={!SHAREABLE_FLOW_STATUSES.has(flow.status) || isCopying}
          >
            <Link2 className="h-4 w-4" />
            Copiar enlace del formulario
          </Button>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection separated={false}>
        <SidePanelSectionBody>
          <span className="text-xs font-medium uppercase text-muted-foreground">Historial</span>
          <FlowAuditTimeline flowId={flow.id} clientName={clientName} />
        </SidePanelSectionBody>
      </SidePanelSection>
    </div>
  )
}
